import React, { Component, cloneElement, type ReactElement, type MouseEvent } from 'react';
import { styled, css } from '@compiled/react';
import debounce from 'lodash/debounce';
import { token } from '@atlaskit/tokens';
import { isVisualRegressionTest } from '@atlassian/jira-platform-test-utils-visual-regression/src/common/utils.tsx';
import { CHART_SCROLLBARS_Z_INDEX } from '../../../../../styles/z-index.tsx';
import type { Pixels } from '../../../../../types';
import createRaf from '../../../../../utils/create-request-animation-frame.tsx';
import { TIMELINE_ID } from '../../../../../utils/timeline-scroller.tsx';
import type { OnDrag } from '../../../common/components/drag-observer/types.tsx';
import withDragObserver from '../../../common/components/drag-observer/view.tsx';
import { RESIZE_DEBOUNCE_MS } from '../../../common/constants';
import withSynchronizedScroll from '../../../common/context/scroll-synchronizer/consumer/index.tsx';
import type { ViewportContextProps } from '../../../common/context/viewport/context/index.tsx';
import withViewportContext from '../../../common/context/viewport/with-viewport-context/index.tsx';
import getScrollbarSize from '../../../common/utils/get-scrollbar-size';
import { getThumbSize, getThumbOffset, getScrollFromPosition } from './utils';

const THUMB_DRAG_THRESHOLD = 0.1;

const requestAnimationFrame = createRaf();

type Ref = HTMLElement | null;

type ScrollbarChildren = ReactElement;

type APIProps = {
	isHideScrollbar: boolean;
	timelineDefaultPosition: number;
	children: ScrollbarChildren;
};

type Props = APIProps & ViewportContextProps;

type State = {
	scrollbarSize: number;
	horizontalThumbSize: number;
	verticalThumbSize: number;
	isHorizontalOverflow: boolean;
	isVerticalOverflow: boolean;
	isHorizontalThumbBeingDragged: boolean;
	isVerticalThumbBeingDragged: boolean;
};

type Snapshot = {
	contentWidth: number;
	contentHeight: number;
};

// eslint-disable-next-line jira/react/no-class-components
class Scrollbars extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		const scrollbarSize = getScrollbarSize();

		this.state = {
			scrollbarSize,
			verticalThumbSize: 0,
			horizontalThumbSize: 0,
			isHorizontalOverflow: false,
			isVerticalOverflow: false,
			isHorizontalThumbBeingDragged: false,
			isVerticalThumbBeingDragged: false,
		};
	}

	componentDidMount() {
		if (this.state.scrollbarSize > 0) {
			window.addEventListener('resize', this.onWindowResize);
			// We want to update the dimensions ASAP, before the user sees them
			this.scheduleUpdateCustomScrollParameters();
		}

		// for some unknown reason the VR refuses to scroll the timeline to the right
		// it also breaks the nav indicators so we turn it off the VR tests
		if (!isVisualRegressionTest) this.setScrollLeft(this.props.timelineDefaultPosition);
	}

	getSnapshotBeforeUpdate(prevProps: Props) {
		// If children have changed, store their dimensions to check on update
		if (this.contentRef && prevProps.children !== this.props.children) {
			const { clientWidth, clientHeight } = this.contentRef;
			return { contentWidth: clientWidth, contentHeight: clientHeight };
		}

		return null;
	}

	componentDidUpdate(prevProps: Props, prevState: State, snapshot: Snapshot | null) {
		const { timelineDefaultPosition } = this.props;
		const { scrollbarSize } = this.state;

		// If the children's dimensions have changed, we need to update the scroll parameters
		if (scrollbarSize > 0 && snapshot !== null && this.contentRef) {
			const { clientWidth, clientHeight } = this.contentRef;
			if (clientWidth !== snapshot.contentWidth || clientHeight !== snapshot.contentHeight) {
				this.scheduleUpdateCustomScrollParameters();
			}
		}

		// Each timeline mode has a different default position to scroll to
		if (this.containerRef && timelineDefaultPosition !== prevProps.timelineDefaultPosition) {
			this.setScrollLeft(timelineDefaultPosition);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onWindowResize);
	}

	// ====================== //
	// === EVENT HANDLING === //
	// ====================== //

	onWindowResize = debounce(() => {
		this.scheduleUpdateCustomScrollParameters();
	}, RESIZE_DEBOUNCE_MS);

	onScroll = () => {
		const { horizontalThumbSize, verticalThumbSize } = this.state;

		this.setHorizontalOffset(horizontalThumbSize);
		this.setVerticalOffset(verticalThumbSize);
	};

	onDragHorizontalThumb: OnDrag = (from, to) => this.setScrollLeftFromPosition(to.x);

	onStartDragHorizontalThumb = () => this.setState({ isHorizontalThumbBeingDragged: true });

	onEndDragHorizontalThumb = () => this.setState({ isHorizontalThumbBeingDragged: false });

	onDragVerticalThumb: OnDrag = (from, to) => this.setScrollTopFromPosition(to.y);

	onStartDragVerticalThumb = () => this.setState({ isVerticalThumbBeingDragged: true });

	onEndDragVerticalThumb = () => this.setState({ isVerticalThumbBeingDragged: false });

	onMouseDownHorizontalThumb = (event: MouseEvent) => {
		if (this.horizontalThumbRef) {
			const { left } = this.horizontalThumbRef.getBoundingClientRect();
			const relativeMousePosition = event.clientX - left;
			this.horizontalThumbOffset = Math.round(
				this.state.horizontalThumbSize / 2 - relativeMousePosition,
			);
		} else {
			this.horizontalThumbOffset = 0;
		}
	};

	onMouseDownVerticalThumb = (event: MouseEvent) => {
		if (this.verticalThumbRef) {
			const { top } = this.verticalThumbRef.getBoundingClientRect();
			const relativeMousePosition = event.clientY - top;
			this.verticalThumbOffset = Math.round(
				this.state.verticalThumbSize / 2 - relativeMousePosition,
			);
		} else {
			this.verticalThumbOffset = 0;
		}
	};

	onMouseDownHorizontalTrack = (event: MouseEvent<HTMLElement>) => {
		this.horizontalThumbOffset = 0;
		this.setScrollLeftFromPosition(event.pageX);
	};

	onMouseDownVerticalTrack = (event: MouseEvent<HTMLElement>) => {
		this.verticalThumbOffset = 0;
		this.setScrollTopFromPosition(event.pageY);
	};

	// ============ //
	// === REFS === //
	// ============ //

	// eslint-disable-next-line jira/react-no-unneeded-arrow-function-properties
	onSetContainerRef = (elem: Ref) => {
		if (elem) {
			this.containerRef = elem;
		}
	};

	onSetContentRef = (elem: Ref) => {
		if (elem) {
			this.contentRef = elem;
		}
	};

	onSetHorizontalTrackRef = (elem: Ref) => {
		if (elem) {
			this.horizontalTrackRef = elem;
		}
	};

	onSetHorizontalThumbRef = (elem: Ref) => {
		if (elem) {
			this.horizontalThumbRef = elem;
		}
	};

	onSetVerticalTrackRef = (elem: Ref) => {
		if (elem) {
			this.verticalTrackRef = elem;
		}
	};

	onSetVerticalThumbRef = (elem: Ref) => {
		if (elem) {
			this.verticalThumbRef = elem;
		}
	};

	// ========================= //
	// === GETTERS / SETTERS === //
	// ========================= //

	getHorizontalThumbSize(): number {
		if (!this.containerRef || !this.horizontalTrackRef) {
			throw new Error('An element required to get the horizontal thumb size was null');
		}

		const { scrollWidth, clientWidth: containerWidth } = this.containerRef;
		const { clientWidth: horizontalTrackWidth } = this.horizontalTrackRef;

		return getThumbSize(containerWidth, scrollWidth, horizontalTrackWidth);
	}

	getVerticalThumbSize(): number {
		if (!this.containerRef || !this.verticalTrackRef) {
			throw new Error('An element required to get the vertical thumb size was null');
		}

		const { scrollHeight, clientHeight: containerHeight } = this.containerRef;
		const { clientHeight: verticalTrackHeight } = this.verticalTrackRef;

		return getThumbSize(containerHeight, scrollHeight, verticalTrackHeight);
	}

	// Get the offset relative to the track for the custom horizontal thumb
	setHorizontalOffset(horizontalThumbSize: number) {
		if (!this.containerRef || !this.horizontalTrackRef || !this.horizontalThumbRef) {
			throw new Error('An element required to get the horizontal thumb offset was null');
		}

		const { scrollLeft, scrollWidth, clientWidth: containerWidth } = this.containerRef;
		const { clientWidth: horizontalTrackWidth } = this.horizontalTrackRef;

		// Set the style directly, we want to avoid a render cycle of the children

		this.horizontalThumbRef.style.transform = `translateX(${getThumbOffset(
			scrollLeft,
			scrollWidth,
			containerWidth,
			horizontalTrackWidth,
			horizontalThumbSize,
		)}px)`;
	}

	// Get the offset relative to the track for the custom vertical thumb
	setVerticalOffset(verticalThumbSize: number) {
		if (!this.containerRef || !this.verticalTrackRef || !this.verticalThumbRef) {
			throw new Error('An element required to get the vertical thumb offset was null');
		}

		const { scrollTop, scrollHeight, clientHeight: containerHeight } = this.containerRef;
		const { clientHeight: verticalTrackHeight } = this.verticalTrackRef;

		// Set the style directly, we want to avoid a render cycle of the children

		this.verticalThumbRef.style.transform = `translateY(${getThumbOffset(
			scrollTop,
			scrollHeight,
			containerHeight,
			verticalTrackHeight,
			verticalThumbSize,
		)}px)`;
	}

	// Set the scroll left on the real scrollbar based on a pageX position from click/drag
	setScrollLeftFromPosition(position: Pixels) {
		if (!this.containerRef || !this.horizontalTrackRef || !this.contentRef) {
			throw new Error('An element required to set the scroll left position was null');
		}

		const { horizontalThumbSize } = this.state;
		const { clientWidth: contentWidth } = this.contentRef;
		const { left: trackLeft, width: trackWidth } = this.horizontalTrackRef.getBoundingClientRect();

		this.containerRef.scrollLeft = getScrollFromPosition(
			position + this.horizontalThumbOffset,
			horizontalThumbSize,
			contentWidth,
			trackWidth,
			trackLeft,
		);
	}

	// Set the scroll top on the real scrollbar based on a pageY position from click/drag
	setScrollTopFromPosition(position: Pixels) {
		if (!this.containerRef || !this.verticalTrackRef || !this.contentRef) {
			throw new Error('An element required to set the scroll top position was null');
		}

		const { verticalThumbSize } = this.state;
		const { clientHeight: contentHeight } = this.contentRef;
		const { top: trackTop, height: trackHeight } = this.verticalTrackRef.getBoundingClientRect();

		this.containerRef.scrollTop = getScrollFromPosition(
			position + this.verticalThumbOffset,
			verticalThumbSize,
			contentHeight,
			trackHeight,
			trackTop,
		);
	}

	// Not to be confused with setScrollLeftFromPosition. This is for raw offset values.
	setScrollLeft(left: Pixels) {
		if (this.containerRef) {
			this.containerRef.scrollLeft = left;
		}
	}

	updateCustomScrollParameters() {
		// Track/thumb refs may not be *required* because there may be no overflow
		if (!this.containerRef || !this.contentRef) {
			throw new Error('An element required to get the custom scroll parameters was null');
		}

		const { clientWidth: contentWidth, clientHeight: contentHeight } = this.contentRef;
		const { clientWidth: containerWidth, clientHeight: containerHeight } = this.containerRef;

		let horizontalParameters = { isHorizontalOverflow: false };
		let verticalParameters = { isVerticalOverflow: false };

		// Content is wider than viewport
		if (contentWidth > containerWidth) {
			const horizontalThumbSize = this.getHorizontalThumbSize();

			this.setHorizontalOffset(horizontalThumbSize);

			horizontalParameters = {
				...horizontalParameters,
				// @ts-expect-error - TS2322 - Type '{ horizontalThumbSize: number; isHorizontalOverflow: true; }' is not assignable to type '{ isHorizontalOverflow: boolean; }'.
				horizontalThumbSize,
				isHorizontalOverflow: true,
			};
		}

		// Content is higher than viewport
		if (contentHeight > containerHeight) {
			const verticalThumbSize = this.getVerticalThumbSize();

			this.setVerticalOffset(verticalThumbSize);

			verticalParameters = {
				...verticalParameters,
				// @ts-expect-error - TS2322 - Type '{ verticalThumbSize: number; isVerticalOverflow: true; }' is not assignable to type '{ isVerticalOverflow: boolean; }'.
				verticalThumbSize,
				isVerticalOverflow: true,
			};
		}

		this.setState({
			...horizontalParameters,
			...verticalParameters,
		});
	}

	scheduleUpdateCustomScrollParameters() {
		requestAnimationFrame(() => {
			this.updateCustomScrollParameters();
		});
	}

	// @ts-expect-error - TS2564 - Property 'containerRef' has no initializer and is not definitely assigned in the constructor.
	containerRef: Ref;

	// @ts-expect-error - TS2564 - Property 'contentRef' has no initializer and is not definitely assigned in the constructor.
	contentRef: Ref;

	// @ts-expect-error - TS2564 - Property 'horizontalTrackRef' has no initializer and is not definitely assigned in the constructor.
	horizontalTrackRef: Ref;

	// @ts-expect-error - TS2564 - Property 'horizontalThumbRef' has no initializer and is not definitely assigned in the constructor.
	horizontalThumbRef: Ref;

	// @ts-expect-error - TS2564 - Property 'verticalTrackRef' has no initializer and is not definitely assigned in the constructor.
	verticalTrackRef: Ref;

	// @ts-expect-error - TS2564 - Property 'verticalThumbRef' has no initializer and is not definitely assigned in the constructor.
	verticalThumbRef: Ref;

	// @ts-expect-error - TS2564 - Property 'horizontalThumbOffset' has no initializer and is not definitely assigned in the constructor.
	horizontalThumbOffset: number;

	// @ts-expect-error - TS2564 - Property 'verticalThumbOffset' has no initializer and is not definitely assigned in the constructor.
	verticalThumbOffset: number;

	renderCustomTrack() {
		const { isHideScrollbar } = this.props;
		const {
			horizontalThumbSize,
			verticalThumbSize,
			isHorizontalOverflow,
			isVerticalOverflow,
			isHorizontalThumbBeingDragged,
			isVerticalThumbBeingDragged,
		} = this.state;

		return (
			<>
				<HorizontalTrack
					ref={this.onSetHorizontalTrackRef}
					isVisible={isHorizontalOverflow && !isHideScrollbar}
					isTrackThumbBeingDragged={isHorizontalThumbBeingDragged}
					onMouseDown={this.onMouseDownHorizontalTrack}
				>
					<DraggableHorizontalThumb
						onDrag={this.onDragHorizontalThumb}
						onDragStart={this.onStartDragHorizontalThumb}
						onDragEnd={this.onEndDragHorizontalThumb}
						size={horizontalThumbSize}
						ref={this.onSetHorizontalThumbRef}
						// @ts-expect-error - TS2322 - Type '(event: MouseEvent) => void' is not assignable to type 'OnMouseDown'.
						onMouseDown={this.onMouseDownHorizontalThumb}
					/>
				</HorizontalTrack>
				<VerticalTrack
					ref={this.onSetVerticalTrackRef}
					isVisible={isVerticalOverflow && !isHideScrollbar}
					isTrackThumbBeingDragged={isVerticalThumbBeingDragged}
					onMouseDown={this.onMouseDownVerticalTrack}
				>
					<DraggableVerticalThumb
						onDrag={this.onDragVerticalThumb}
						onDragStart={this.onStartDragVerticalThumb}
						onDragEnd={this.onEndDragVerticalThumb}
						size={verticalThumbSize}
						ref={this.onSetVerticalThumbRef}
						// @ts-expect-error - TS2322 - Type '(event: MouseEvent) => void' is not assignable to type 'OnMouseDown'.
						onMouseDown={this.onMouseDownVerticalThumb}
					/>
				</VerticalTrack>
			</>
		);
	}

	onInnerRef = (elem: Ref) => {
		const { setRef: setViewportRef } = this.props;
		setViewportRef(elem);
		this.onSetContainerRef(elem);
	};

	render() {
		const { children } = this.props;
		const { scrollbarSize } = this.state;

		return (
			<CustomScrollbarsContainer>
				<SynchronizedScrollbar
					// @ts-expect-error - Property 'id' does not exist on type 'IntrinsicAttributes & ((Props | Omit<Props & RefAttributes<Component<Props, any, any>>, "ref">) & RefAttributes<...>)'.
					id={TIMELINE_ID}
					ref={this.onInnerRef}
					scrollbarSize={scrollbarSize}
					onScroll={scrollbarSize > 0 ? this.onScroll : undefined}
				>
					{cloneElement(children, { ref: this.onSetContentRef })}
				</SynchronizedScrollbar>
				{scrollbarSize > 0 && this.renderCustomTrack()}
			</CustomScrollbarsContainer>
		);
	}
}

const CustomScrollbars = withViewportContext<APIProps>(Scrollbars);
export default CustomScrollbars;

// =========================== //
// === CUSTOM SCROLL VIEWS === //
// =========================== //

const CUSTOM_SCROLLBAR_SIZE = '10px';
// Stops overlap when scrolled to the end of both tracks
const CUSTOM_SCROLLBAR_OFFSET = '7px';

interface BaseTrackStylesProps {
	isVisible: boolean;
	isTrackThumbBeingDragged: boolean;
}

const baseTrackStyles = css({
	position: 'absolute',
	borderRadius: CUSTOM_SCROLLBAR_SIZE,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isVisible }: BaseTrackStylesProps) =>
		isVisible ? 'visibility:visible' : 'visibility:hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_SCROLLBARS_Z_INDEX,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isTrackThumbBeingDragged }: BaseTrackStylesProps) =>
		isTrackThumbBeingDragged
			? `backgroundColor: ${token('color.background.inverse.subtle.pressed', 'rgba(0,0,0,0.3)')}`
			: '',
	'&:hover': {
		transition: 'background 0.1s ease-in',
		backgroundColor: token('color.background.inverse.subtle.pressed', 'rgba(0,0,0,0.3)'),
	},
});

const baseThumbStyles = css({
	position: 'relative',
	borderRadius: CUSTOM_SCROLLBAR_SIZE,
	backgroundColor: token('color.background.inverse.subtle.pressed', 'rgba(0,0,0,0.4)'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const VerticalTrack = styled.div<BaseTrackStylesProps>(baseTrackStyles, {
	top: CUSTOM_SCROLLBAR_OFFSET,
	bottom: CUSTOM_SCROLLBAR_OFFSET,
	width: CUSTOM_SCROLLBAR_SIZE,
	right: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const HorizontalTrack = styled.div<BaseTrackStylesProps>(baseTrackStyles, {
	right: CUSTOM_SCROLLBAR_OFFSET,
	left: CUSTOM_SCROLLBAR_OFFSET,
	height: CUSTOM_SCROLLBAR_SIZE,
	bottom: 0,
});

interface ThumbProps {
	size: number;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const VerticalThumb = styled.div<ThumbProps>(baseThumbStyles, {
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${({ size }: ThumbProps) => size}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const HorizontalThumb = styled.div<ThumbProps>(baseThumbStyles, {
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${({ size }: ThumbProps) => size}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomScrollbarsContainer = styled.div({
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HiddenScrollbarPanel = styled.div<{ scrollbarSize: number }>(
	{
		position: 'absolute',
		top: 0,
		right: 0,
		left: 0,
		bottom: 0,
		overflow: 'scroll',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ scrollbarSize }: { scrollbarSize: number }) =>
		scrollbarSize > 0 && `right: -${scrollbarSize}px; bottom: -${scrollbarSize}px;`,
);

const DraggableHorizontalThumb = withDragObserver(HorizontalThumb, THUMB_DRAG_THRESHOLD);
const DraggableVerticalThumb = withDragObserver(VerticalThumb, THUMB_DRAG_THRESHOLD);
// @ts-expect-error - TS2345 - Argument of type 'ComponentType<{ scrollbarSize: number; } & ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps>' is not assignable to parameter of type 'ComponentType<Props>'
const SynchronizedScrollbar = withSynchronizedScroll(HiddenScrollbarPanel, {
	isCompiledComponent: true,
	syncAxis: undefined,
});
